import { useId, type SelectHTMLAttributes } from 'react';
import { Icon } from './Icon';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'className'> {
  label?: string;
  options: readonly SelectOption[];
  error?: string;
  helpText?: string;
  placeholder?: string;
}

/**
 * Port of components/forms/Select.jsx.
 *
 * The source took `options` as an array of strings, which forces the visible
 * label to double as the stored value — unusable for anything keyed by id, such
 * as choosing an account. Options are {value, label} here.
 */
export function Select({
  label,
  options,
  error,
  helpText,
  placeholder,
  id,
  ...rest
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const messageId = `${selectId}-message`;
  const message = error ?? helpText;

  return (
    <div className={[styles.field, error ? styles.error : null].filter(Boolean).join(' ')}>
      {label ? (
        <label className={styles.label} htmlFor={selectId}>
          {label}
        </label>
      ) : null}

      <div className={styles.wrap}>
        <select
          id={selectId}
          className={styles.select}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" size={16} color="var(--text-tertiary)" className={styles.chevron} />
      </div>

      {message ? (
        <span className={error ? styles.errorMessage : styles.message} id={messageId}>
          {message}
        </span>
      ) : null}
    </div>
  );
}
